<script lang="ts" setup>
import { ref, onMounted, reactive } from 'vue'
import { AppInitializationDto, AuthProvider, EventBus } from '@mit/aws-vue'
import { getAppConfig } from './composables/auth'
import { AuthService } from './services/AuthService'
import ConfirmDialog from 'primevue/confirmdialog'

AuthService.getInstance()

const initializeDto = reactive<AppInitializationDto>({
    isLoading: true,
    hasAccess: false,
    hasError: false
})

let sessionExpired = ref(false)
let isLoggedIn = ref(false)

const authProvider = new AuthProvider(getAppConfig())

const onSessionExpired = (expired: boolean) => {
    sessionExpired.value = expired
}

onMounted(() => {
    EventBus.subscribe('SESSION_EXPIRED', onSessionExpired)
    init()
})

const initializeAuthProvider = async (): Promise<void> => {
    let token: string | null = null

    try {
        await authProvider.configure()
        token = await authProvider.getToken(true)
    } catch (error) {
        try {
            token = await authProvider.getToken(false)
            if (!token) {
                throw error
            }
        } catch (tokenError) {
            console.error('Failed to get existing token:', tokenError)
            throw error
        }
    }

    if (!sessionExpired.value && token) {
        window.localStorage.setItem('accessToken', token)
        isLoggedIn.value = true
    } else {
        throw new Error('No valid authentication token available')
    }
}

const init = async () => {
    try {
        await initializeAuthProvider()
    } catch (error) {
        console.error('App initialization failed:', error)
        initializeDto.isLoading = false
        initializeDto.hasAccess = false
        initializeDto.hasError = true
        isLoggedIn.value = false

        try {
            await authProvider.signIn()
        } catch (signInError) {
            console.error('Failed to trigger sign-in:', signInError)
        }
    }
}
</script>

<template>
    <ConfirmDialog />
    <div v-if="!isLoggedIn" class="flex items-center justify-center min-h-screen">
        <div class="text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-[32px]"></div>
            <p class="display-medium text-black/75">MIT Architecture Dashboard</p>
        </div>
    </div>
    <router-view v-else />
</template>

<style scoped></style>
